import { LitElement, html, css } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { api } from '../../api';
import { store } from '../../store';
import { showToast } from '../../components/ui/toast';
import { t } from '../../i18n';

const MAX_DETAILS_BYTES = 256 * 1024;

/**
 * <cadence-event-details> — the Details tab for an event. The event's "details document"
 * (`details_md` Markdown stored on the event row) is edited here with the visual editor
 * (<cadence-rich-text-editor>), or imported from a .md file (kept from the old upload).
 * Students read and accept this before registering interest. Editing is gated by
 * events.edit; without it, the document is shown read-only. Persists via PUT /events/:id
 * (a partial update of details_md/details_filename) and emits `saved` so the host page can
 * refresh its copy. See [[contenteditable-toolbar-no-native-select]] for the editor.
 */
@customElement('cadence-event-details')
export class CadenceEventDetails extends LitElement {
  @property() eventId = '';
  @property({ attribute: false }) detailsMd: string | null = null;
  @property() detailsFilename: string | null = null;

  @state() private md = '';
  @state() private filename: string | null = null;
  @state() private saving = false;
  @state() private dirty = false;
  @query('input[type=file]') private fileInput!: HTMLInputElement;

  private inited = false;
  private readonly canEdit = store.hasPermission('events.edit');

  static styles = css`
    :host { display: block; }
    .panel { margin-top: var(--space-4); }
    .toolbar { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; margin-bottom: var(--space-2); }
    .left, .right { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
    .fn { color: var(--color-text-secondary); font-size: 0.82rem; }
    .hint { color: var(--color-text-secondary); font-size: 0.85rem; margin: 0 0 var(--space-3); }
  `;

  // Initialize the working copy from the host-provided props once. The component is
  // recreated each time the tab is opened, so this re-reads the latest values.
  willUpdate(changed: Map<string, unknown>) {
    if (!this.inited && (changed.has('detailsMd') || changed.has('detailsFilename'))) {
      this.md = this.detailsMd ?? '';
      this.filename = this.detailsFilename ?? null;
      this.inited = true;
    }
  }

  private async onFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (file.size > MAX_DETAILS_BYTES) { showToast(t('events.toast.detailsTooLarge'), 'error'); input.value = ''; return; }
    try {
      this.md = await file.text();
      this.filename = file.name;
      this.dirty = true;
    } catch { showToast(t('events.toast.fileReadError'), 'error'); }
    input.value = '';
  }

  private clear() { this.md = ''; this.filename = null; this.dirty = true; }

  private async save() {
    this.saving = true;
    try {
      const md = this.md.trim() ? this.md : null;
      const filename = md ? this.filename : null;
      await api.put(`/events/${this.eventId}`, { details_md: md, details_filename: filename });
      this.dirty = false;
      showToast(t('events.toast.detailsSaved'), 'success');
      this.dispatchEvent(new CustomEvent('saved', { detail: { details_md: md, details_filename: filename }, bubbles: true, composed: true }));
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.saving = false; }
  }

  render() {
    if (!this.canEdit) {
      return html`<div class="panel">
        ${this.md.trim()
          ? html`<cadence-markdown-renderer .markdown=${this.md}></cadence-markdown-renderer>`
          : html`<cadence-empty-state heading=${t('events.detailsDoc.empty.heading')} message=${t('events.detailsDoc.empty.message')}></cadence-empty-state>`}
      </div>`;
    }
    return html`<div class="panel">
      <div class="toolbar">
        <div class="left">
          <input type="file" accept=".md,.markdown,text/markdown,text/plain" hidden @change=${(e: Event) => this.onFile(e)} />
          <cadence-button variant="secondary" @click=${() => this.fileInput.click()}>${t('events.detailsDoc.importMd')}</cadence-button>
          ${this.filename ? html`<span class="fn">${this.filename}</span>` : ''}
        </div>
        <div class="right">
          ${this.md.trim() ? html`<cadence-button variant="ghost" @click=${() => this.clear()}>${t('events.actions.remove')}</cadence-button>` : ''}
          <cadence-button ?loading=${this.saving} ?disabled=${!this.dirty} @click=${() => this.save()}>${t('events.detailsDoc.save')}</cadence-button>
        </div>
      </div>
      <p class="hint">${t('events.detailsDoc.hint')}</p>
      <cadence-rich-text-editor .value=${this.md} min-height="320"
        placeholder=${t('events.detailsDoc.placeholder')}
        @input=${(e: CustomEvent) => { this.md = e.detail; this.dirty = true; }}></cadence-rich-text-editor>
    </div>`;
  }
}
